import { Events } from "@/src/config";
import useAuthStore from "@/src/store/authStore";
import { isAuthSessionInvalidated } from "@/src/util/auth-session";
import { subscribe, unsubscribe } from "@/src/util/event";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import AppToast from "../components/AppToast";
import { useToast } from "./use-toast";

const resolveAuthMessage = (t: ReturnType<typeof useTranslations>, messageKey?: string) => {
  const key = messageKey ?? "error.auth.session_expired";
  try {
    return t(key as "error.auth.session_expired");
  } catch {
    return key;
  }
};

const useAuthRedirect = () => {
  const pathname = usePathname();
  const t = useTranslations();
  const { showToast } = useToast();
  const authStore = useAuthStore();
  const handlingRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = async (e: Event) => {
      if (handlingRef.current) {
        return;
      }
      handlingRef.current = true;

      const { message } = (e as CustomEvent<{ message?: string }>).detail || {};
      showToast(
        <AppToast title={resolveAuthMessage(t, message)} />,
        {
          position: "bottom-end",
          intent: "warning",
        },
        true,
      );
      await authStore.clear();

      if (pathname !== "/login" && isAuthSessionInvalidated()) {
        window.setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      }
    };

    subscribe(Events.InvalidToken, handler);
    return () => unsubscribe(Events.InvalidToken, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
};

export default useAuthRedirect;
